import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Fade,
  IconButton,
  MenuItem,
  Paper,
  Popper,
  Select,
  Typography,
} from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useClientTimeZone } from '../../../hooks/useClientTimeZone';
import { formatTransactionTimestamp, getTransactionDisplayTimestamp } from '../../../utils/formatTransactionTimestamp';
import { ACCOUNT_COLORS, accountCardSx } from '../../../components/account/accountPageStyles';
import { AccountButton } from '../../../components/account/AccountButton';
import { CWY_NETWORK, explorerAddressUrl, explorerTxUrl } from '../config/cwyConfig';
import type { CwyTransferRequest } from '../services/walletService';
import { useDelayedHoverPopper, HOVER_CARD_OPEN_DELAY_MS } from '../hooks/useDelayedHoverPopper';
import {
  DEFAULT_TRANSACTION_LOG_FILTERS,
  exportTransactionsCsv,
  filterAndSortTransactions,
  hasActiveFilters,
  paginateTransactions,
  requestTypeLabel,
  type TransactionLogFilters,
  type TransactionPageSize,
  type TransactionSortOrder,
  type TransactionStatusFilter,
  type TransactionTimeframe,
  type TransactionTypeFilter,
} from '../utils/transactionLogFilters';

interface CwyActivityLedgerProps {
  requests: CwyTransferRequest[];
}

const STATUS_LABELS: Record<CwyTransferRequest['status'], string> = {
  pending: 'Pending approval',
  processing: 'Processing on-chain',
  completed: 'Completed',
  rejected: 'Rejected',
  failed: 'Failed',
};

const STATUS_COLORS: Record<
  CwyTransferRequest['status'],
  { bg: string; color: string; border: string }
> = {
  pending: { bg: 'rgba(0, 113, 133, 0.1)', color: ACCOUNT_COLORS.teal, border: 'rgba(0, 113, 133, 0.25)' },
  processing: { bg: 'rgba(0, 113, 133, 0.1)', color: ACCOUNT_COLORS.teal, border: 'rgba(0, 113, 133, 0.25)' },
  completed: { bg: ACCOUNT_COLORS.successDim, color: ACCOUNT_COLORS.success, border: 'rgba(55,203,139,.25)' },
  rejected: { bg: ACCOUNT_COLORS.goldBright, color: '#FFFFFF', border: ACCOUNT_COLORS.goldBright },
  failed: { bg: ACCOUNT_COLORS.dangerBg, color: ACCOUNT_COLORS.danger, border: ACCOUNT_COLORS.dangerBorder },
};

const selectSx = {
  minWidth: 0,
  fontSize: 12.5,
  fontWeight: 600,
  fontFamily: ACCOUNT_COLORS.fontBody,
  color: ACCOUNT_COLORS.text,
  bgcolor: ACCOUNT_COLORS.surface2,
  borderRadius: '8px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: ACCOUNT_COLORS.borderSoft },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ACCOUNT_COLORS.border },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ACCOUNT_COLORS.goldBright },
  '& .MuiSelect-select': { py: 0.875, px: 1.25 },
};

function formatAmount(amount: number): string {
  const trimmed = amount.toFixed(9).replace(/\.?0+$/, '');
  return trimmed || '0';
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.4px',
          textTransform: 'uppercase',
          color: ACCOUNT_COLORS.textDim,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function DetailRow({
  label,
  value,
  mono,
  onCopy,
  copied,
  href,
}: {
  label: string;
  value: string;
  mono?: boolean;
  onCopy?: () => void;
  copied?: boolean;
  href?: string;
}) {
  return (
    <Box sx={{ mb: 1.25, '&:last-of-type': { mb: 0 } }}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          color: ACCOUNT_COLORS.textDim,
          mb: 0.375,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        {href ? (
          <Box
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={{
              flex: 1,
              fontFamily: mono ? ACCOUNT_COLORS.fontMono : ACCOUNT_COLORS.fontBody,
              fontSize: 11.5,
              lineHeight: 1.45,
              wordBreak: 'break-all',
              color: ACCOUNT_COLORS.text,
              textDecoration: 'none',
              '&:hover': { color: ACCOUNT_COLORS.goldBright },
            }}
          >
            {value}
          </Box>
        ) : (
          <Typography
            sx={{
              flex: 1,
              fontFamily: mono ? ACCOUNT_COLORS.fontMono : ACCOUNT_COLORS.fontBody,
              fontSize: 11.5,
              lineHeight: 1.45,
              wordBreak: 'break-all',
              color: value === '—' ? ACCOUNT_COLORS.textDim : ACCOUNT_COLORS.text,
            }}
          >
            {value}
          </Typography>
        )}
        {onCopy && value !== '—' && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onCopy();
            }}
            aria-label={copied ? `${label} copied` : `Copy ${label}`}
            sx={{
              width: 24,
              height: 24,
              mt: -0.25,
              flexShrink: 0,
              color: copied ? ACCOUNT_COLORS.success : ACCOUNT_COLORS.textDim,
              bgcolor: copied ? ACCOUNT_COLORS.successDim : 'transparent',
              transition: 'color .15s ease, background-color .15s ease',
              '&:hover': {
                bgcolor: copied ? ACCOUNT_COLORS.successDim : ACCOUNT_COLORS.surface2,
              },
            }}
          >
            {copied ? (
              <CheckIcon sx={{ fontSize: 15 }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            )}
          </IconButton>
        )}
      </Box>
    </Box>
  );
}

function ActivityRow({ request, timeZone }: { request: CwyTransferRequest; timeZone: string }) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const { open, scheduleOpen, scheduleClose, openNow } = useDelayedHoverPopper({
    openDelayMs: HOVER_CARD_OPEN_DELAY_MS,
  });
  const [copiedField, setCopiedField] = useState<'wallet' | 'tx' | 'id' | null>(null);

  const isDeposit = request.request_type === 'treasury_deposit';
  const sign = isDeposit ? '−' : '+';
  const amountClass = isDeposit ? ACCOUNT_COLORS.text : ACCOUNT_COLORS.success;
  const statusStyle = STATUS_COLORS[request.status];
  const typeLabel = requestTypeLabel(request.request_type);

  const copyText = async (text: string, field: 'wallet' | 'tx' | 'id') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      window.setTimeout(() => setCopiedField(null), 2000);
    } catch {
      setCopiedField(null);
    }
  };

  return (
    <>
      <Box
        ref={triggerRef}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.625,
          py: 1.625,
          px: 0.5,
          borderBottom: `1px solid ${ACCOUNT_COLORS.borderSoft}`,
          borderRadius: '8px',
          cursor: 'default',
          transition: 'background-color .15s ease',
          '&:last-of-type': { borderBottom: 'none' },
          '&:hover': { bgcolor: ACCOUNT_COLORS.surface2 },
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: '9px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            bgcolor: isDeposit ? ACCOUNT_COLORS.goldDim : ACCOUNT_COLORS.successDim,
            color: isDeposit ? ACCOUNT_COLORS.goldBright : ACCOUNT_COLORS.success,
          }}
        >
          {isDeposit ? (
            <ArrowUpwardIcon sx={{ fontSize: 15 }} />
          ) : (
            <ArrowDownwardIcon sx={{ fontSize: 15 }} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{typeLabel}</Typography>
          <Typography
            sx={{
              fontFamily: ACCOUNT_COLORS.fontMono,
              fontSize: 11.5,
              color: ACCOUNT_COLORS.textDim,
              mt: 0.25,
            }}
          >
            {formatTransactionTimestamp(getTransactionDisplayTimestamp(request), timeZone)}
            {request.tx_signature && (
              <Box component="span" sx={{ ml: 0.75, color: ACCOUNT_COLORS.teal }}>
                · On-chain
              </Box>
            )}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: ACCOUNT_COLORS.fontMono,
            fontSize: 14,
            fontWeight: 600,
            color: amountClass,
            textAlign: 'right',
          }}
        >
          {sign}
          {request.amount_cwy} CWY
        </Typography>
        <Box
          component="span"
          sx={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.3px',
            textTransform: 'uppercase',
            px: 1,
            py: 0.375,
            borderRadius: '100px',
            bgcolor: statusStyle.bg,
            color: statusStyle.color,
            border: `1px solid ${statusStyle.border}`,
            whiteSpace: 'nowrap',
          }}
        >
          {request.status}
        </Box>
      </Box>

      <Popper
        open={open && Boolean(triggerRef.current)}
        anchorEl={triggerRef.current}
        placement="left"
        transition
        modifiers={[
          { name: 'offset', options: { offset: [0, 8] } },
          { name: 'preventOverflow', options: { padding: 12 } },
        ]}
        sx={{ zIndex: 1500 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={180}>
            <Paper
              elevation={0}
              onMouseEnter={openNow}
              onMouseLeave={scheduleClose}
              sx={{
                width: 320,
                p: 2,
                mr: 0.5,
                borderRadius: ACCOUNT_COLORS.radius,
                border: `1px solid ${ACCOUNT_COLORS.borderSoft}`,
                boxShadow: '0 12px 40px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.06)',
                pointerEvents: 'auto',
              }}
            >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.75 }}>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: ACCOUNT_COLORS.text }}>
                {typeLabel}
              </Typography>
              <Typography
                sx={{
                  fontFamily: ACCOUNT_COLORS.fontMono,
                  fontSize: 16,
                  fontWeight: 700,
                  color: amountClass,
                  mt: 0.5,
                }}
              >
                {sign}
                {formatAmount(request.amount_cwy)} CWY
              </Typography>
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.3px',
                textTransform: 'uppercase',
                px: 1,
                py: 0.5,
                borderRadius: '100px',
                bgcolor: statusStyle.bg,
                color: statusStyle.color,
                border: `1px solid ${statusStyle.border}`,
                whiteSpace: 'nowrap',
              }}
            >
              {STATUS_LABELS[request.status]}
            </Box>
          </Box>

          <DetailRow label="Network" value={CWY_NETWORK} />
          <DetailRow
            label="Wallet address"
            value={request.wallet_address}
            mono
            copied={copiedField === 'wallet'}
            onCopy={() => void copyText(request.wallet_address, 'wallet')}
            href={explorerAddressUrl(request.wallet_address)}
          />
          <DetailRow
            label="Request ID"
            value={request.transfer_request_id}
            mono
            copied={copiedField === 'id'}
            onCopy={() => void copyText(request.transfer_request_id, 'id')}
          />
          <DetailRow label="Requested" value={formatTransactionTimestamp(request.createdAt, timeZone)} />
          {request.reviewed_at && (
            <DetailRow label="Reviewed" value={formatTransactionTimestamp(request.reviewed_at, timeZone)} />
          )}
          {request.tx_confirmed_at && (
            <DetailRow label="Confirmed on-chain" value={formatTransactionTimestamp(request.tx_confirmed_at, timeZone)} />
          )}
          <DetailRow
            label="Transaction signature"
            value={request.tx_signature ?? '—'}
            mono
            copied={copiedField === 'tx'}
            onCopy={
              request.tx_signature ? () => void copyText(request.tx_signature!, 'tx') : undefined
            }
            href={request.tx_signature ? explorerTxUrl(request.tx_signature) : undefined}
          />

          {request.status === 'rejected' && request.rejection_reason && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: ACCOUNT_COLORS.radiusSm,
                bgcolor: ACCOUNT_COLORS.dangerBg,
                border: `1px solid ${ACCOUNT_COLORS.dangerBorder}`,
              }}
            >
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: ACCOUNT_COLORS.danger, mb: 0.5 }}>
                REJECTION REASON
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: ACCOUNT_COLORS.text, lineHeight: 1.45 }}>
                {request.rejection_reason}
              </Typography>
            </Box>
          )}

          {request.status === 'failed' && request.tx_error && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.25,
                borderRadius: ACCOUNT_COLORS.radiusSm,
                bgcolor: ACCOUNT_COLORS.dangerBg,
                border: `1px solid ${ACCOUNT_COLORS.dangerBorder}`,
              }}
            >
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: ACCOUNT_COLORS.danger, mb: 0.5 }}>
                TRANSACTION ERROR
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: ACCOUNT_COLORS.text, lineHeight: 1.45 }}>
                {request.tx_error}
              </Typography>
            </Box>
          )}

          {request.tx_signature && (
            <Box
              component="a"
              href={explorerTxUrl(request.tx_signature)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                mt: 1.75,
                pt: 1.5,
                borderTop: `1px solid ${ACCOUNT_COLORS.borderSoft}`,
                width: '100%',
                fontSize: 12.5,
                fontWeight: 600,
                color: ACCOUNT_COLORS.goldBright,
                textDecoration: 'none',
                '&:hover': { color: ACCOUNT_COLORS.gold },
              }}
            >
              <OpenInNewIcon sx={{ fontSize: 15 }} />
              View on Solana Explorer
            </Box>
          )}
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
}

export const CwyActivityLedger: React.FC<CwyActivityLedgerProps> = ({ requests }) => {
  const timeZone = useClientTimeZone();
  const [filters, setFilters] = useState<TransactionLogFilters>(DEFAULT_TRANSACTION_LOG_FILTERS);

  const updateFilters = (patch: Partial<TransactionLogFilters>) => {
    setFilters((prev) => {
      const resetsPage =
        patch.timeframe !== undefined ||
        patch.typeFilter !== undefined ||
        patch.statusFilter !== undefined ||
        patch.pageSize !== undefined ||
        patch.sortOrder !== undefined;

      return {
        ...prev,
        ...patch,
        page: patch.page !== undefined ? patch.page : resetsPage ? 1 : prev.page,
      };
    });
  };

  const filtered = useMemo(() => filterAndSortTransactions(requests, filters), [requests, filters]);
  const { pageItems, totalPages, total, safePage } = useMemo(
    () => paginateTransactions(filtered, filters.pageSize, filters.page),
    [filtered, filters.pageSize, filters.page]
  );

  useEffect(() => {
    if (safePage !== filters.page) {
      setFilters((prev) => ({ ...prev, page: safePage }));
    }
  }, [safePage, filters.page]);

  const rangeStart = total === 0 ? 0 : filters.pageSize === 'all' ? 1 : (safePage - 1) * filters.pageSize + 1;
  const rangeEnd =
    filters.pageSize === 'all' ? total : Math.min(safePage * (filters.pageSize as number), total);

  return (
    <Box sx={accountCardSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptLongIcon sx={{ fontSize: 16 }} />
            Transaction logs
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: ACCOUNT_COLORS.textDim, mt: 0.5 }}>
            {total === 0
              ? 'No matching transactions'
              : filters.pageSize === 'all'
                ? `Showing all ${total} entries`
                : `Showing ${rangeStart}–${rangeEnd} of ${total}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <AccountButton
            accountVariant="outline"
            size="small"
            onClick={() => exportTransactionsCsv(filtered, timeZone)}
            disabled={filtered.length === 0}
            sx={{ minHeight: 34, fontSize: 12.5, px: 1.5 }}
          >
            <DownloadIcon sx={{ fontSize: 15, mr: 0.75 }} />
            Export CSV
          </AccountButton>
          {hasActiveFilters(filters) && (
            <AccountButton
              accountVariant="ghost"
              size="small"
              onClick={() => setFilters(DEFAULT_TRANSACTION_LOG_FILTERS)}
              sx={{ minHeight: 34, fontSize: 12.5, px: 1.5 }}
            >
              <RestartAltIcon sx={{ fontSize: 15, mr: 0.75 }} />
              Reset
            </AccountButton>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' },
          gap: 1.25,
          mb: 2,
          p: 1.5,
          borderRadius: ACCOUNT_COLORS.radiusSm,
          bgcolor: ACCOUNT_COLORS.surface2,
          border: `1px solid ${ACCOUNT_COLORS.borderSoft}`,
        }}
      >
        <FilterField label="Timeframe">
          <Select
            size="small"
            value={filters.timeframe}
            onChange={(e) => updateFilters({ timeframe: e.target.value as TransactionTimeframe })}
            sx={selectSx}
          >
            <MenuItem value="all">All time</MenuItem>
            <MenuItem value="7d">Last 7 days</MenuItem>
            <MenuItem value="30d">Last 30 days</MenuItem>
            <MenuItem value="90d">Last 90 days</MenuItem>
            <MenuItem value="year">This year</MenuItem>
          </Select>
        </FilterField>

        <FilterField label="Type">
          <Select
            size="small"
            value={filters.typeFilter}
            onChange={(e) => updateFilters({ typeFilter: e.target.value as TransactionTypeFilter })}
            sx={selectSx}
          >
            <MenuItem value="all">All types</MenuItem>
            <MenuItem value="cwy_grant">CWY grants</MenuItem>
            <MenuItem value="vault_deposit">Vault deposits</MenuItem>
            <MenuItem value="on_chain">On-chain (Solana)</MenuItem>
            <MenuItem value="off_chain">Off-chain only</MenuItem>
          </Select>
        </FilterField>

        <FilterField label="Status">
          <Select
            size="small"
            value={filters.statusFilter}
            onChange={(e) => updateFilters({ statusFilter: e.target.value as TransactionStatusFilter })}
            sx={selectSx}
          >
            <MenuItem value="all">All statuses</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FilterField>

        <FilterField label="Sort">
          <Select
            size="small"
            value={filters.sortOrder}
            onChange={(e) => updateFilters({ sortOrder: e.target.value as TransactionSortOrder })}
            sx={selectSx}
          >
            <MenuItem value="desc">Newest first</MenuItem>
            <MenuItem value="asc">Oldest first</MenuItem>
          </Select>
        </FilterField>

        <FilterField label="Rows per page">
          <Select
            size="small"
            value={filters.pageSize}
            onChange={(e) => {
              const value = e.target.value;
              updateFilters({
                pageSize: (value === 'all' ? 'all' : Number(value)) as TransactionPageSize,
              });
            }}
            sx={selectSx}
          >
            <MenuItem value={5}>5 rows</MenuItem>
            <MenuItem value={10}>10 rows</MenuItem>
            <MenuItem value={25}>25 rows</MenuItem>
            <MenuItem value="all">Show all</MenuItem>
          </Select>
        </FilterField>
      </Box>

      {pageItems.length === 0 ? (
        <Typography sx={{ fontSize: 13.5, color: ACCOUNT_COLORS.textLo }}>
          {requests.length === 0
            ? 'No CWY transactions yet. Request test tokens or make a vault deposit.'
            : 'No transactions match your filters. Try adjusting timeframe or type.'}
        </Typography>
      ) : (
        pageItems.map((request) => (
          <ActivityRow key={request.transfer_request_id} request={request} timeZone={timeZone} />
        ))
      )}

      {filters.pageSize !== 'all' && total > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: 2,
            pt: 1.75,
            borderTop: `1px solid ${ACCOUNT_COLORS.borderSoft}`,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: 12, color: ACCOUNT_COLORS.textDim }}>
            Page {safePage} of {totalPages}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <AccountButton
              accountVariant="outline"
              size="small"
              disabled={safePage <= 1}
              onClick={() => updateFilters({ page: safePage - 1 })}
              sx={{ minHeight: 32, fontSize: 12 }}
            >
              Previous
            </AccountButton>
            <AccountButton
              accountVariant="outline"
              size="small"
              disabled={safePage >= totalPages}
              onClick={() => updateFilters({ page: safePage + 1 })}
              sx={{ minHeight: 32, fontSize: 12 }}
            >
              Next
            </AccountButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};
